// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/shortLinks.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/shortLinks.js
//
// invits.co links, in one place: how they are built and how they are read.
//
// ── Short for sharing, long still honoured ─────────────────────────────────
//
//   invite  https://invits.co/i/priyas-30th        (was /invite/priyas-30th)
//   card    https://invits.co/c/happy-bday-ritu    (was /card/happy-bday-ritu)
//   pass    https://invits.co/i/<eventId>/pass/<id>
//
// Everything new is built short: a shorter link is less of a wall in a group
// chat and a less dense QR code on a ticket. Every link already sent — in
// WhatsApp threads, on printed tickets, in old notifications — uses the long
// form, and those keep working forever. So READING accepts both, and nothing
// that reads a link may assume only one.
//
// ── Why these never collide with usernames ─────────────────────────────────
//
// A profile is one segment: invits.co/ritu. A short invite is two: /i/<slug>.
// "i" and "c" are only link types when a second segment follows, so someone
// whose handle is literally "i" still gets their profile at invits.co/i.

export const DEFAULT_DOMAIN = 'https://invits.co';

/** Short segment -> the long segment it stands for. */
export const LINK_ALIASES = { i: 'invite', c: 'card' };

function clean(domain) {
  return String(domain || DEFAULT_DOMAIN).replace(/\/+$/, '');
}

/** https://invits.co/i/<slug> */
export function inviteUrl(slug, domain) {
  return `${clean(domain)}/i/${slug}`;
}

/** https://invits.co/c/<slug> */
export function cardUrl(slug, domain) {
  return `${clean(domain)}/c/${slug}`;
}

/** https://invits.co/i/<eventId>/pass/<purchaseId> — the ticket QR. */
export function passUrl(eventId, purchaseId, domain) {
  return `${clean(domain)}/i/${eventId}/pass/${purchaseId}`;
}

/**
 * Rewrites a short path to the long path it stands for; anything else is
 * returned untouched.
 *
 *   '/i/abc'           -> '/invite/abc'
 *   'c/abc?ref=x'      -> 'card/abc?ref=x'
 *   '/i'               -> '/i'            (a profile called "i")
 *   '/ritu'            -> '/ritu'
 *
 * Existing routing understands the long form, so expanding at the edge means
 * none of it has to learn a second spelling.
 */
export function expandShortPath(path) {
  if (typeof path !== 'string') return path;
  const m = /^(\/?)([ic])\/([^/?#]+)(.*)$/.exec(path);
  if (!m) return path;
  const [, lead, short, next, rest] = m;
  return `${lead}${LINK_ALIASES[short]}/${next}${rest}`;
}

/**
 * Reads any invits.co link, short or long, into what it points at.
 *
 * @returns {{kind: 'invite', slug: string}
 *         | {kind: 'card', slug: string}
 *         | {kind: 'pass', eventId: string, purchaseId: string}
 *         | null}
 */
export function parseInvitsLink(input) {
  const parts = splitInvitsUrl(input);
  if (!parts || parts.segments.length < 2) return null;

  const segs = parts.segments;
  const type = LINK_ALIASES[segs[0]] ?? segs[0];

  if (type === 'invite' && segs[2] === 'pass' && segs[3]) {
    return { kind: 'pass', eventId: segs[1], purchaseId: segs[3] };
  }
  if (type === 'invite') return { kind: 'invite', slug: segs[1] };
  if (type === 'card' || type === 'cards') return { kind: 'card', slug: segs[1] };
  return null;
}

/**
 * Splits an invits.co link into its origin and path segments, or null.
 *
 * A hand-rolled parse rather than `new URL()`: React Native's URL is a set of
 * regexes that behaves differently from a browser's or Node's (it does not
 * throw on garbage, and appends a trailing slash), so tests run under Node
 * would not be testing what runs on the phone. This behaves the same on both.
 */
function splitInvitsUrl(input) {
  if (typeof input !== 'string') return null;
  const m = /^(?:(https?):\/\/)?((?:www\.)?invits\.co)(?=[/?#]|$)([^?#]*)/i.exec(input.trim());
  if (!m) return null;
  const [, scheme, host, path] = m;
  return {
    origin: `${(scheme || 'https').toLowerCase()}://${host.toLowerCase()}`,
    segments: path.split('/').filter(Boolean),
  };
}

/**
 * Finds the first invits.co invite or card link inside a block of text — a
 * chat message, say — in either form.
 */
export function findInvitsLink(text) {
  if (typeof text !== 'string') return null;
  const re = /(?:https?:\/\/)?(?:www\.)?invits\.co\/(?:i|c|invite|cards?)\/[^\s)>\]]+/gi;
  for (const match of text.matchAll(re)) {
    const parsed = parseInvitsLink(match[0].replace(/[.,!?;:'"]+$/, ''));
    if (parsed) return parsed;
  }
  return null;
}

/** The short form of a long invits.co link, for display. Unknown links pass through. */
export function shortenInvitsLink(link) {
  const parsed = parseInvitsLink(link);
  if (!parsed) return link;
  const origin = splitInvitsUrl(link).origin;
  if (parsed.kind === 'invite') return inviteUrl(parsed.slug, origin);
  if (parsed.kind === 'card') return cardUrl(parsed.slug, origin);
  return passUrl(parsed.eventId, parsed.purchaseId, origin);
}
